import { Link } from 'wouter';
import { trialDaysLeft } from '@shared/entitlements';
import { useT } from '../i18n';
import { useSession } from '../state/session';
import { symbolImage } from '@shared/symbols';
import { Dialog } from './Dialog';
import { Notice } from './Notice';
import { Pictogram } from './Pictogram';

const icon = (emoji: string, name: string) => (
  <Pictogram source={{ icon: emoji, image: symbolImage(`ui-${name}.webp`) }} className="panel-btn__icon" />
);

interface Props {
  open: boolean;
  onClose(): void;
  onEdit(): void;
  onSettings(): void;
  onSubscribe(): void;
  onLock(): void;
  onLogout(): void;
}

/** Everything that is not communication, in one place behind the PIN. */
export function AdultPanel({ open, onClose, onEdit, onSettings, onSubscribe, onLock, onLogout }: Props) {
  const t = useT();
  const { user, config } = useSession();
  if (!user) return null;

  const days = trialDaysLeft(user);
  const plan = user.isTest
    ? `🧪 ${t('planTest')}`
    : user.effectivePlan === 'standard'
      ? `⭐ ${t('planStandard')}`
      : user.effectivePlan === 'free_trial'
        ? `🎁 ${t('planTrialDays', { n: days })}`
        : t('planFree');

  return (
    <Dialog open={open} onClose={onClose} title={t('adultArea')}>
      <div className="panel">
        <p className="panel__account">
          <strong>{user.name}</strong>
          <span className="badge">{plan}</span>
        </p>
        {user.plan === 'standard' && user.paymentStatus === 'inactive' && <Notice kind="warning">{t('paymentIssue')}</Notice>}

        <div className="panel__actions">
          <button type="button" className="panel-btn" onClick={onEdit}>
            {icon('✏️', 'edit')} {t('editBoard')}
          </button>
          <button type="button" className="panel-btn" onClick={onSettings}>
            {icon('🔊', 'voice')} {t('voiceAndScreen')}
          </button>
          {user.effectivePlan !== 'standard' && !user.isTest && config?.billing.enabled && (
            <button type="button" className="panel-btn panel-btn--primary" onClick={onSubscribe}>
              {icon('⭐', 'subscribe')} {t('subscribe')}
            </button>
          )}
          <Link href="/perfil" className="panel-btn">
            {icon('🧑', 'profile')} {t('profile')}
          </Link>
          {user.isAdmin && (
            <Link href="/admin" className="panel-btn">
              {icon('🛠️', 'admin')} {t('admin')}
            </Link>
          )}
          <button type="button" className="panel-btn" onClick={onLogout}>
            {icon('🚪', 'logout')} {t('logout')}
          </button>
          <button type="button" className="panel-btn panel-btn--lock" onClick={onLock}>
            {icon('🔒', 'lock')} {t('lock')}
          </button>
        </div>
        <p className="attribution small">{t('attribution')}</p>
      </div>
    </Dialog>
  );
}
